import React from 'react';
import { useTranslation } from 'react-i18next';
import { BFDrawer } from '../../../components/BFDrawer';
import { useGetCompanyByIdQuery } from '../../../store/api/company';
import { useGetBranchesQuery } from '../../../store/api/branch';
import BFButton from '../../../components/button/BFButton';
import { CompanyStatus } from '../../../constants/enums';
import type { BranchDto } from '../../../types/branch';
import type { CompanyDto } from '../../../types/company';
import './CompanyDetailDrawer.css';

interface Props {
    companyId: number | null;
    onClose: () => void;
}

function buildAddress(parts: Array<string | undefined | null>): string {
    return parts.filter(Boolean).join(', ');
}

function statusBadgeClass(status: CompanyStatus): string {
    if (status === CompanyStatus.Active || status === CompanyStatus.ActiveValidated) {
        return 'cd-badge cd-badge--active';
    }
    if (status === CompanyStatus.Blocked || status === CompanyStatus.BlockedAuto) {
        return 'cd-badge cd-badge--inactive';
    }
    return 'cd-badge cd-badge--neutral';
}

function DetailRow({ label, value }: { label: string; value?: React.ReactNode }) {
    if (value === undefined || value === null || value === '' || value === '—') return null;
    return (
        <>
            <span className="cd-detail-label">{label}</span>
            <span className="cd-detail-value">{value}</span>
        </>
    );
}

function BranchCard({ branch }: { branch: BranchDto }) {
    const address = buildAddress([branch.street, branch.municipality || branch.village, branch.zipCode]);
    return (
        <div className="cd-branch-card">
            <div className="cd-branch-card__name">{branch.name}</div>
            {address && <div className="cd-branch-card__address">{address}</div>}
        </div>
    );
}

function CompanyInfo({ company }: { company: CompanyDto }) {
    const { t } = useTranslation();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const te = (key: string) => t(key as any);

    const address = buildAddress([
        company.street,
        company.municipality || company.village || company.cityDistrict,
        company.zipCode,
        company.county,
    ]);

    return (
        <>
            <div className="cd-company-name">{company.name}</div>
            <div className="cd-detail-grid">
                <DetailRow
                    label={t('tables.companyTable.status')}
                    value={
                        <span className={statusBadgeClass(company.status)}>
                            {te(`enums.companyStatus.${company.status}`)}
                        </span>
                    }
                />
                <DetailRow label={t('selectedCompany.companyDetail.ic')} value={company.ic} />
                <DetailRow label={t('selectedCompany.companyDetail.dic')} value={company.vat} />
                <DetailRow label={t('tables.companyTable.registrationId')} value={company.registrationId} />
                <DetailRow
                    label={t('tables.companyTable.legalFormId')}
                    value={company.legalForm ? te(`enums.companyLegalForm.${company.legalForm}`) : undefined}
                />
                <DetailRow
                    label={t('tables.companyTable.size')}
                    value={company.size ? te(`enums.companySize.${company.size}`) : undefined}
                />
                <DetailRow label={t('selectedCompany.generic.state')} value={company.country} />
                <DetailRow label={t('tables.companyTable.mainScopeId')} value={company.mainScope} />
                <DetailRow
                    label={t('tables.companyTable.establishedDate')}
                    value={company.establishedDate ? new Date(company.establishedDate).toLocaleDateString() : undefined}
                />
                <DetailRow
                    label={t('tables.companyTable.capitalAmount')}
                    value={company.capitalAmount != null ? company.capitalAmount.toLocaleString() : undefined}
                />
                <DetailRow label={t('tables.companyTable.address')} value={address || undefined} />
                <DetailRow
                    label={t('tables.companyTable.registrationLastChange')}
                    value={
                        company.registrationLastChange
                            ? new Date(company.registrationLastChange).toLocaleDateString()
                            : undefined
                    }
                />
                {company.terminationType && (
                    <DetailRow
                        label={t('tables.companyTable.terminationType')}
                        value={te(`enums.companyTerminationType.${company.terminationType}`)}
                    />
                )}
                {company.terminationDate && (
                    <DetailRow
                        label={t('tables.companyTable.terminationDate')}
                        value={new Date(company.terminationDate).toLocaleDateString()}
                    />
                )}
            </div>
        </>
    );
}

export function CompanyDetailDrawer({ companyId, onClose }: Props) {
    const { t } = useTranslation();

    const {
        data: company,
        isFetching: companyFetching,
        isError: companyError,
        refetch: refetchCompany,
    } = useGetCompanyByIdQuery(companyId!, { skip: companyId === null });

    const {
        data: branchesResult,
        isFetching: branchesFetching,
        isError: branchesError,
        refetch: refetchBranches,
    } = useGetBranchesQuery({ filters: { companyId: [companyId!] }, pageSize: 100 }, { skip: companyId === null });

    const renderBody = () => {
        if (companyFetching) {
            return (
                <div className="cd-state">
                    <div className="cd-spinner" aria-hidden="true" />
                    <span>{t('generic.loading')}</span>
                </div>
            );
        }

        if (companyError || !company) {
            return (
                <div className="cd-state">
                    <span className="cd-error-icon" aria-hidden="true">
                        ⚠
                    </span>
                    <p className="cd-error-msg">{t('errors.somethingWentWrong')}</p>
                    <BFButton size="small" color="accent" onClick={() => refetchCompany()}>
                        {t('selectedCompany.companyDetail.retry')}
                    </BFButton>
                </div>
            );
        }

        const renderBranchesContent = () => {
            if (branchesFetching) {
                return (
                    <div className="cd-state">
                        <div className="cd-spinner" aria-hidden="true" />
                    </div>
                );
            }
            if (branchesError) {
                return (
                    <div className="cd-state">
                        <span className="cd-error-icon" aria-hidden="true">
                            ⚠
                        </span>
                        <p className="cd-error-msg">{t('errors.somethingWentWrong')}</p>
                        <BFButton size="small" color="accent" onClick={() => refetchBranches()}>
                            {t('selectedCompany.companyDetail.retry')}
                        </BFButton>
                    </div>
                );
            }
            const branches = branchesResult?.items ?? [];
            if (branches.length === 0) {
                return <p className="cd-no-branches">{t('selectedCompany.companyDetail.noBranches')}</p>;
            }
            return (
                <div className="cd-branch-list">
                    {branches.map((b) => (
                        <BranchCard key={b.id} branch={b} />
                    ))}
                </div>
            );
        };

        return (
            <>
                <div className="cd-section">
                    <CompanyInfo company={company} />
                </div>

                <div className="cd-section">
                    <h3 className="cd-section-title">{t('selectedCompany.branches.companyBranches')}</h3>
                    {renderBranchesContent()}
                </div>
            </>
        );
    };

    return (
        <BFDrawer
            open={companyId !== null}
            title={t('selectedCompany.companyDetail.compDetail')}
            onClose={onClose}
            width="38%"
        >
            {renderBody()}
        </BFDrawer>
    );
}

export default CompanyDetailDrawer;
